import { memo, ReactNode } from 'react';
import { CircularProgress, FormHelperText, MenuItem } from '@mui/material';
import { SelectProps } from '@mui/material/Select/Select';
import { IOption } from '@shared/interfaces';
import { InputLabel, SelectWrapper, StyledSelect } from '@styles/InputStyles';

export interface ISelectProps<T = unknown> extends Omit<SelectProps, 'onChange'> {
  label?: string;
  isErrorShown?: boolean;
  value: string | number | null;
  options: IOption<T>[];
  onChange(value: unknown): void;
  helperText?: ReactNode;
  error?: boolean;
  isLoading?: boolean;
  required?: boolean;
  displayError?: boolean;
  fullWidth?: boolean;
  dense?: boolean;
  disabled?: boolean;
  hideNoneOption?: boolean;
  placeholder?: string;
  SelectInputWrapper?: React.ElementType;
}

export interface ISelectOption {
  id: string;
  name: string;
}

const SelectBase = <T = unknown,>({
  label,
  value = '',
  isErrorShown = true,
  options,
  isLoading,
  onChange,
  helperText,
  displayError,
  fullWidth,
  required,
  dense,
  error,
  disabled,
  hideNoneOption,
  placeholder,
  SelectInputWrapper,
  ...rest
}: ISelectProps<T>) => {
  const valueIsMissing = !value && value !== 0 && value !== '';
  const errorRequired = displayError && required && valueIsMissing;
  const errorRequiredText = errorRequired && `${label} is required`;
  const GeneralSelectWrapper = SelectInputWrapper ? SelectInputWrapper : SelectWrapper;

  return (
    <GeneralSelectWrapper labeled={Boolean(label)}>
      {label && <InputLabel>{label}</InputLabel>}

      <StyledSelect
        displayEmpty
        disabled={disabled}
        error={error}
        value={value || ''}
        fullWidth={fullWidth}
        dense={dense}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={({ target: { value } }) => onChange(value)}
        {...rest}
      >
        {!hideNoneOption && (
          <MenuItem value="">
            <em>{placeholder || 'None'}</em>
          </MenuItem>
        )}

        {isLoading && (
          <MenuItem value="" disabled={true}>
            <CircularProgress color="inherit" size={16} />
          </MenuItem>
        )}

        {options.map((option, index) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <MenuItem key={index} disabled={option.disabled} value={option.value as any}>
            {option.label}
          </MenuItem>
        ))}
      </StyledSelect>

      {!!(helperText || errorRequiredText) && isErrorShown && (
        <FormHelperText error={error}>{helperText || errorRequiredText}</FormHelperText>
      )}
    </GeneralSelectWrapper>
  );
};

export const Select = memo(SelectBase);
